import React, { useState, useEffect } from 'react'
import { Zap, Plus, TrendingUp, Clock } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { getUserCredits } from '../../services/stripe'
import SubscriptionModal from '../Subscription/SubscriptionModal'

interface CreditBalanceProps {
  showPurchaseButton?: boolean
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

const CreditBalance: React.FC<CreditBalanceProps> = ({ 
  showPurchaseButton = true, 
  size = 'md',
  className = '' 
}) => {
  const { user } = useAuth()
  const [credits, setCredits] = useState<number>(0)
  const [loading, setLoading] = useState(true)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)

  useEffect(() => {
    if (user) {
      loadCredits()
    }
  }, [user])

  const loadCredits = async () => {
    if (!user) return
    
    try {
      setLoading(true)
      const userCredits = await getUserCredits(user.id)
      setCredits(userCredits)
    } catch (error) {
      console.error('Error loading credits:', error)
    } finally {
      setLoading(false)
    }
  }

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return {
          container: 'p-2',
          icon: 'w-4 h-4',
          text: 'text-sm',
          number: 'text-lg',
          button: 'px-2 py-1 text-xs'
        }
      case 'lg':
        return {
          container: 'p-6',
          icon: 'w-8 h-8',
          text: 'text-lg',
          number: 'text-3xl',
          button: 'px-6 py-3 text-base'
        }
      default: // md
        return {
          container: 'p-4',
          icon: 'w-6 h-6',
          text: 'text-base',
          number: 'text-2xl',
          button: 'px-4 py-2 text-sm'
        }
    }
  }

  const sizeClasses = getSizeClasses()

  if (loading) {
    return (
      <div className={`bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200 ${sizeClasses.container} ${className}`}>
        <div className="flex items-center space-x-3">
          <div className={`bg-yellow-100 rounded-full p-2 animate-pulse`}>
            <Zap className={`${sizeClasses.icon} text-yellow-600`} />
          </div>
          <div className="flex-1">
            <div className={`h-4 bg-yellow-200 rounded animate-pulse mb-1`}></div>
            <div className={`h-6 bg-yellow-200 rounded animate-pulse w-16`}></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className={`bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200 ${sizeClasses.container} ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-yellow-100 rounded-full p-2">
              <Zap className={`${sizeClasses.icon} text-yellow-600`} />
            </div>
            <div>
              <p className={`${sizeClasses.text} font-medium text-gray-700`}>
                Image Credits
              </p>
              <p className={`${sizeClasses.number} font-bold text-gray-900`}>
                {credits.toLocaleString()}
              </p>
            </div>
          </div>
          
          {showPurchaseButton && (
            <div className="flex flex-col space-y-2">
              <button
                onClick={() => setShowSubscriptionModal(true)}
                className={`flex items-center space-x-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-medium transition-colors ${sizeClasses.button}`}
              >
                <Plus className="w-4 h-4" />
                <span>Buy Credits</span>
              </button>
              
              {credits === 0 && (
                <div className="flex items-center space-x-1 text-xs text-orange-600">
                  <Clock className="w-3 h-3" />
                  <span>No credits left</span>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Credit Usage Tips */}
        {size !== 'sm' && (
          <div className="mt-4 pt-4 border-t border-yellow-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-600">
              <div className="flex items-center space-x-2">
                <Zap className="w-3 h-3 text-yellow-500" />
                <span>1 credit = 1 AI image</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-3 h-3 text-blue-500" />
                <span>Credits never expire</span>
              </div>
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-3 h-3 text-green-500" />
                <span>Save up to 40% on bulk purchases</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Subscription Modal */}
      <SubscriptionModal 
        isOpen={showSubscriptionModal}
        onClose={() => {
          setShowSubscriptionModal(false)
          // Reload credits when modal closes
          loadCredits()
        }}
      />
    </>
  )
}

export default CreditBalance 